import type {
  PreparedInMemory,
  PreparedOrigin,
} from '@react-apps/content-editor';

/**
 * Persistência do lote de importação, para ele sobreviver a um F5 ou a sair da
 * página no meio.
 *
 * A fila vive em memória: sem isto, um refresh some com a lista do que subiu e
 * do que faltou — e a prova já existe, pela metade, no servidor. Não dá para
 * reconstruir isso pela tela, porque as questões que faltam são justamente as
 * que não estão lá, e o zip original já não está mais no browser.
 *
 * Dois registros com ritmos diferentes, de propósito:
 *
 * - `FILES_STORE` é escrito **uma vez** por lote e carrega o peso (o `.zip`
 *   inteiro e as imagens convertidas/trocadas). É o que dispensa pedir o
 *   arquivo de novo na hora de reenviar.
 * - `STATE_STORE` é reescrito a cada mudança da fila — 160 vezes numa prova de
 *   80 questões — então guarda só `{position, status}` por questão.
 *
 * IndexedDB e não `sessionStorage`: o zip tem megabytes e o teto do
 * `sessionStorage` é de ~5 MB.
 */

const DB_NAME = 'admin-dashboard';
const DB_VERSION = 1;
const FILES_STORE = 'exam-import-files';
const STATE_STORE = 'exam-import-state';

/** Lote velho não interessa mais a ninguém — e o zip ocupa espaço de verdade. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface ImportQuestionState {
  position: number;
  /**
   * `done` = subiu. Qualquer outra coisa volta para a fila no reenvio.
   *
   * `waiting-retry` é **legado**: só existe em lotes gravados antes de a fila
   * deixar de reenviar sozinha. Fica no tipo porque descreve o que pode estar
   * no IndexedDB, não o que a fila produz hoje — e, como todo não-`done`, volta
   * para a fila.
   */
  status: 'queued' | 'processing' | 'waiting-retry' | 'done' | 'failed';
  error?: string;
}

export interface ImportState {
  examId: string;
  /** Como a prova aparece na tela: "(ENARE-DF) … · Clínica Médica 2026". */
  examLabel: string;
  startedAt: number;
  questions: ImportQuestionState[];
}

export interface ImportFiles {
  examId: string;
  /** O `.zip` como veio. Reaberto no reenvio para remontar as questões. */
  zip: Blob;
  /** Imagens que não estão no zip: as convertidas e as trocadas à mão. */
  overrides: {
    filename: string;
    blob: Blob;
    mime: PreparedInMemory['mime'];
    /**
     * Opcional porque a sessão gravada antes de 2026-10-26 guardava a frase
     * (`note`) em vez do motivo; quem reidrata cai em "trocado" quando falta.
     */
    origin?: PreparedOrigin;
  }[];
}

/**
 * A imagem corrigida que a sessão guardou, de volta ao formato do pré-flight.
 *
 * A sessão gravada antes de 2026-10-26 não tem `origin`, só a frase (`note`),
 * e cai em `REPLACED`: o `origin` reidratado só alimenta o upload e a próxima
 * gravação, e a frase da imagem corrigida nunca é mostrada depois de um F5.
 */
export function preparedFromOverride({
  blob,
  mime,
  origin,
}: ImportFiles['overrides'][number]): PreparedInMemory {
  return {
    source: 'memory',
    blob,
    mime,
    origin: origin ?? { kind: 'REPLACED', mime },
  };
}

// ---------------------------------------------------------------------------
// IndexedDB, o mínimo
// ---------------------------------------------------------------------------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(FILES_STORE)) {
        db.createObjectStore(FILES_STORE, { keyPath: 'examId' });
      }
      if (!db.objectStoreNames.contains(STATE_STORE)) {
        db.createObjectStore(STATE_STORE, { keyPath: 'examId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexedDB'));
  });
}

function run<T>(
  store: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const request = work(tx.objectStore(store));
        request.onsuccess = () => resolve(request.result as T);
        request.onerror = () => reject(request.error ?? new Error(store));
        tx.oncomplete = () => db.close();
      }),
  );
}

/**
 * Persistir é conveniência, nunca requisito: em aba anônima, com quota
 * estourada ou com o banco bloqueado por outra aba, a tela perde a capacidade
 * de **retomar** — não a de importar. Por isso toda falha aqui morre num
 * `console.warn`.
 */
async function quiet<T>(work: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await work();
  } catch (err) {
    console.warn('[exam-import] persistência indisponível', err);
    return fallback;
  }
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export function saveImportFiles(files: ImportFiles): Promise<void> {
  return quiet(
    () => run<void>(FILES_STORE, 'readwrite', (store) => store.put(files)),
    undefined,
  );
}

export function saveImportState(state: ImportState): Promise<void> {
  return quiet(
    () => run<void>(STATE_STORE, 'readwrite', (store) => store.put(state)),
    undefined,
  );
}

export function loadImportFiles(examId: string): Promise<ImportFiles | null> {
  return quiet(
    () =>
      run<ImportFiles | undefined>(FILES_STORE, 'readonly', (store) =>
        store.get(examId),
      ).then((found) => found ?? null),
    null,
  );
}

/** O lote inacabado mais recente, se houver. Lotes velhos são descartados. */
export function loadResumableImport(): Promise<ImportState | null> {
  return quiet(async () => {
    const all = await run<ImportState[]>(STATE_STORE, 'readonly', (store) =>
      store.getAll(),
    );
    const cutoff = Date.now() - MAX_AGE_MS;
    const stale = all.filter((state) => state.startedAt < cutoff);
    for (const state of stale) await clearImport(state.examId);

    const unfinished = all
      .filter((state) => state.startedAt >= cutoff)
      .filter((state) => state.questions.some((q) => q.status !== 'done'))
      .sort((a, b) => b.startedAt - a.startedAt);
    return unfinished[0] ?? null;
  }, null);
}

/** Lote fechado (ou descartado pelo admin): o zip não precisa sobreviver. */
export async function clearImport(examId: string): Promise<void> {
  await quiet(
    () => run<void>(STATE_STORE, 'readwrite', (store) => store.delete(examId)),
    undefined,
  );
  await quiet(
    () => run<void>(FILES_STORE, 'readwrite', (store) => store.delete(examId)),
    undefined,
  );
}
